// The spike's combat values from its content tables (decision 0061: tuning is data): the moves with their frame data
// (`moves`), the weapons' move sets (`weapons`), the stat set (`stats`), each fighter's values (`fighters`), the dojo's
// dummies (`dojo`), the telegraph styles (`telegraph-styles`, the feedback stream's, which draws them) and the rules (the tuning table's `combat.*` rows), turned into
// @rpg/combat's shapes. Cross-table rules the schemas can't say (a move's telegraph style exists; every combo chains)
// are checked here and reported as issues.
import type { AttributeDef, EffectDef } from "@rpg/abilities";
import { checkMoves, type CombatRules, type MoveDef, type WeaponDef } from "@rpg/combat";
import type { ContentRegistry } from "@rpg/content";
import { toEffectDef } from "@rpg-unit/status-presets";
import type { ContentTables, DojoPlace, FighterRow, TelegraphStyleRow } from "../content/types.gen";

/** Everything combat reads from the content. */
export interface CombatContent {
  moves: MoveDef[];
  weapons: WeaponDef[];
  /** The attachment each weapon puts on the hand socket (null: none). */
  attachmentOf: ReadonlyMap<string, string | null>;
  stats: AttributeDef[];
  /** The effects moves and items apply (phase D1: the statuses and buffs, the `effects` table). */
  effects: EffectDef[];
  rules: CombatRules;
  fighters: ReadonlyMap<string, FighterRow>;
  dojo: readonly DojoPlace[];
  /** The telegraph styles a move may name (the feedback stream's `telegraph-styles`: the ones drawn). */
  styles: ReadonlyMap<string, TelegraphStyleRow>;
  /** Seconds before a defeated fighter in the dojo stands again. */
  respawn: number;
  /** Problems found across tables (empty when the content holds together). */
  issues: string[];
}

/** Read the combat content. Missing tuning rows throw (the build check keeps them present). */
export function combatContent(reg: ContentRegistry<ContentTables>): CombatContent {
  const tune = (id: string): number => {
    const v = reg.get("tuning", id)?.value;
    if (typeof v !== "number") throw new Error(`combat: no tuning row "${id}"`);
    return v;
  };
  const moves = reg.rows("moves").map(({ label: _label, ...m }) => m as unknown as MoveDef);
  const weapons: WeaponDef[] = reg.rows("weapons").map((w) => ({
    id: w.id,
    combo: w.combo.map((r) => r.id),
    ...(w.skill ? { skill: w.skill.id } : {}),
    ...(w.cooldown !== undefined ? { cooldown: w.cooldown } : {}),
    ...(w.big ? { big: w.big.id } : {}),
    ...(w.bigCost !== undefined ? { bigCost: w.bigCost } : {}),
  }));
  const attachmentOf = new Map(reg.rows("weapons").map((w) => [w.id, w.attachment?.id ?? null] as const));
  const stats: AttributeDef[] = reg.rows("stats").map((s) => ({ id: s.id, base: s.base, ...(s.min !== undefined ? { min: s.min } : {}), ...(s.max !== undefined ? { max: s.max } : {}) }));
  const rules: CombatRules = {
    defenceK: tune("combat.defenceK"),
    parryWindow: tune("combat.parryWindow"),
    parryStun: tune("combat.parryStun"),
    minBlock: tune("combat.minBlock"),
    blockCos: tune("combat.blockCos"),
    chip: tune("combat.chip"),
    blockDrain: tune("combat.blockDrain"),
    guardBreakStun: tune("combat.guardBreakStun"),
    blockHitStopScale: tune("combat.blockHitStopScale"),
    dodge: { seconds: tune("combat.dodge.seconds"), invuln: tune("combat.dodge.invuln"), perfect: tune("combat.dodge.perfect"), speed: tune("combat.dodge.speed"), cost: tune("combat.dodge.cost") },
    counter: { seconds: tune("combat.counter.seconds"), mult: tune("combat.counter.mult") },
    stagger: tune("combat.stagger"),
    poiseRegen: { delay: tune("combat.poiseRegen.delay"), rate: tune("combat.poiseRegen.rate") },
    staminaRegen: { delay: tune("combat.stamina.delay"), rate: tune("combat.stamina.rate"), blocking: tune("combat.stamina.blocking") },
    hitStop: { parry: tune("combat.hitStop.parry"), guardBreak: tune("combat.hitStop.guardBreak"), defeat: tune("combat.hitStop.defeat") },
    slowMo: {
      perfectDodge: { scale: tune("combat.slowMo.perfectDodge.scale"), seconds: tune("combat.slowMo.perfectDodge.seconds"), easeBack: tune("combat.slowMo.perfectDodge.ease") },
      parry: { scale: tune("combat.slowMo.parry.scale"), seconds: tune("combat.slowMo.parry.seconds"), easeBack: tune("combat.slowMo.parry.ease") },
    },
    target: { range: tune("combat.target.range"), halfAngle: tune("combat.target.halfAngle") },
  };
  const styles = new Map(reg.rows("telegraph-styles").map((t) => [t.id, t] as const));
  const effects = effectDefs(reg);
  const issues = checkMoves(moves, weapons, effects.map((e) => e.id));
  for (const m of moves) if (m.telegraph && !styles.has(m.telegraph.style)) issues.push(`${m.id}: no telegraph style "${m.telegraph.style}"`);
  return {
    moves,
    weapons,
    attachmentOf,
    stats,
    effects,
    rules,
    fighters: new Map(reg.rows("fighters").map((f) => [f.id, f] as const)),
    dojo: reg.rows("dojo"),
    styles,
    respawn: tune("combat.respawn"),
    issues,
  };
}

/** The `effects` table as @rpg/abilities' definitions (phase D1: burn, slow, stun, healing over time, buffs). */
export function effectDefs(reg: ContentRegistry<ContentTables>): EffectDef[] {
  // the table is @rpg-unit/status-presets' (the composition framework's first unit), and so is the conversion
  return reg.rows("effects").map(toEffectDef);
}
